import React from 'react';
import { CocoEvent } from '@cotera/client/app/etc';
import { z } from 'zod/v3';
import type { QueryClient } from '@tanstack/react-query';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { Assert } from '@cotera/utilities';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../resources/selection/resource-selection.model';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Button } from '@cotera/client/v0/components/ui/button';
import { FolderContentsPageModel } from './folder-contents-page.model';

type Params = Record<string, never>;

/**
 * Resource lists to refetch after a folder archive. Archiving a folder archives
 * everything inside it, so every list that can show a filed resource is stale.
 */
const ARCHIVE_CASCADE_LIST_TYPES = [
  ResourceType.Chat,
  ResourceType.Agent,
  ResourceType.Workflow,
  ResourceType.Dataset,
  ResourceType.Document,
] as const;

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

type ArchiveFolderClient = Pick<TenantedClient['resource'], 'folder'>;

export class ArchiveFolderAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Archive Folder';
  /**
   * Acts on whatever resource is targeted, so it belongs to that resource's own
   * menu, not to a workspace-wide search. It stays registered and invokable —
   * `discrete` only keeps it out of the palette's list.
   */
  discrete = true;

  description = 'Archive this folder';
  inputSchema = z.object({});
  group = 'app';
  icon = 'archive-box';

  private _lastArchivedFolderIds: string[] = [];

  constructor(
    private readonly client: ArchiveFolderClient,
    private readonly queryClient: QueryClient,
    private readonly navigate: (path: string) => void,
    private readonly orgSlug: string,
    private readonly orgId: string,
    private readonly modalManager: ModalManager,
    private readonly onBeforeNavigate?: () => void
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetFolderIds(context).length > 0;
  }

  getPriority(context: ApplicableContext): number {
    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (
      selection !== null &&
      selection.getResourceIdsForType(ResourceType.Folder).length > 0
    ) {
      return 20;
    }
    return 0;
  }

  shouldAsk(_: ApplicableContext): boolean {
    return true;
  }

  ask(_: {}, context: ExecuteContext): AskResponse<Params> {
    const ids = this.getTargetFolderIds(context);
    Assert.assert(ids.length > 0, 'No folder(s) in scope');
    const subtitle =
      ids.length > 1
        ? `Are you sure you want to archive ${ids.length} folders? All contents will be archived.`
        : 'Are you sure you want to archive this folder? All contents will be archived.';

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => (
            <ArchiveFolderAskView
              subtitle={subtitle}
              onRespond={async (params) => {
                await onRespond(params);
                this.modalManager.pop();
              }}
              onCancel={async () => {
                await onCancel();
                this.modalManager.pop();
              }}
            />
          ),
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }

  async execute(
    _params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const ids = this.getTargetFolderIds(context);
    this._lastArchivedFolderIds = ids;
    if (ids.length === 0) {
      return ok({});
    }

    const folderPage =
      context.targetedModelOfType(FolderContentsPageModel) ??
      context.getInScopeModelOfType(FolderContentsPageModel);

    const loadingToast = toast.loading(
      ids.length > 1
        ? `Archiving ${ids.length} folders...`
        : 'Archiving folder...',
      { id: 'archive-folder' }
    );

    try {
      for (const folderId of ids) {
        const result = await this.client.folder.archive({ folderId });
        Assert.assertOk(result);
        await this.queryClient.removeQueries({
          queryKey: queryKeys.resource.get({
            orgId: this.orgId,
            type: ResourceType.Folder,
            id: folderId,
          }),
        });
      }

      loadingToast.success(
        ids.length > 1 ? `${ids.length} folders archived` : 'Folder archived'
      );

      await this.queryClient.invalidateQueries({
        queryKey: queryKeys.resource.folderTree({ orgId: this.orgId }),
      });
      await this.queryClient.invalidateQueries({
        queryKey: queryKeys.resource.folderList({ orgId: this.orgId }),
      });
      // Every type that can be filed, not just chats and agents: the archive
      // cascades to the folder's whole subtree, so a project full of workflows
      // or datasets left those lists showing rows the server had already
      // archived until something else refetched them.
      for (const type of ARCHIVE_CASCADE_LIST_TYPES) {
        await this.queryClient.invalidateQueries({
          queryKey: queryKeys.resource.list({
            orgId: this.orgId,
            type,
            limit: 50,
          }),
        });
      }
      await this.queryClient.invalidateQueries({
        queryKey: [this.orgId, 'resource', 'folderContents'],
      });
      await this.queryClient.invalidateQueries({
        queryKey: [this.orgId, 'resource', 'unfiledTotals'],
      });

      const selection = context.targetedModelOfType(ResourceSelectionModel);
      if (selection !== null) {
        context.markAsNotTargeted(selection);
      }

      const shouldNavigate =
        folderPage !== null &&
        folderPage.folderId !== null &&
        ids.includes(folderPage.folderId);
      if (shouldNavigate === true) {
        this.onBeforeNavigate?.();
        this.navigate(`/${this.orgSlug}/folders`);
      }

      return ok({});
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to archive folder';
      loadingToast.error(message);
      return err([{ t: 'error', message }]);
    }
  }

  track(): ActionTrackMetadata | null {
    const folderIds = this._lastArchivedFolderIds;
    if (folderIds.length === 0) {
      return null;
    }
    return {
      event: CocoEvent.FolderArchived,
      properties: {
        folderIds,
        count: folderIds.length,
      },
    };
  }

  private getTargetFolderIds(context: ApplicableContext): string[] {
    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection !== null) {
      const folderIds = selection.getResourceIdsForType(ResourceType.Folder);
      if (folderIds.length > 0) {
        return folderIds;
      }

      // If the user targeted non-folder resources (e.g. selecting a chat/agent row
      // from the folder page), we must not fall back to the folder page's current
      // folder. Otherwise Archive can incorrectly apply to the page folder.
      if (selection.selections.length > 0) {
        return [];
      }
    }

    const folderPageModel =
      context.targetedModelOfType(FolderContentsPageModel) ??
      context.getInScopeModelOfType(FolderContentsPageModel);
    if (folderPageModel !== null && folderPageModel.folderId !== null) {
      return [folderPageModel.folderId];
    }

    return [];
  }
}

const ArchiveFolderAskView: React.FC<{
  subtitle: string;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ subtitle, onRespond, onCancel }) => (
  <ModalPageView
    title="Archive Folder"
    subtitle={subtitle}
    className="px-4 py-4"
  >
    <div className="flex flex-col gap-4">
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => void onCancel()}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={() => void onRespond({})}>
          Archive
        </Button>
      </div>
    </div>
  </ModalPageView>
);
